/**
 * @voltxampere/engine — Per-step Newton-Raphson for transient
 *
 * Mirror of `dc/newton-raphson.ts` with two changes:
 *  - Builds the matrix via `buildTransientMNA` (transient stamps
 *    + companion models replace DC's no-op cap and 0-V inductor).
 *  - Carries the previous *step* solution `xStepPrev` (and the step
 *    before that, `xStepPrevPrev`) through every iteration. These
 *    are FIXED across the per-step Newton sweep — they don't update
 *    when the iterate updates. Only the diode-style stamps re-
 *    linearize per iter; the cap / inductor companion is stable.
 *
 * Newton convergence uses the same `isConverged` predicate as DC,
 * so the per-step accuracy floor obeys SPECS §3.4 (`reltol`,
 * `abstol`).
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { ConvergenceError, ContractViolationError } from "@voltxampere/shared";
import { sparseSolve, isFiniteVector } from "@voltxampere/math";
import { applyDeltaVDamping } from "../dc/delta-v-damping.js";
import { REFERENCE_TEMPERATURE_K, vtAtTemperature, } from "../stamps/nonlinear/physical-constants.js";
import { computeResidual, isConvergedReduced } from "../dc/residual.js";
import { buildTransientMNA } from "./mna-builder.js";
export function newtonRaphsonTransient(circuit, plan, opts) {
    if (!Number.isInteger(opts.maxIterations) || opts.maxIterations <= 0) {
        throw new ContractViolationError(`newtonRaphsonTransient: maxIterations must be a positive integer, got ${String(opts.maxIterations)}`);
    }
    if (opts.x0.length !== plan.size) {
        throw new ContractViolationError(`newtonRaphsonTransient: x0 length ${String(opts.x0.length)} ≠ N+M ${String(plan.size)}`);
    }
    if (plan.size === 0) {
        return {
            converged: true,
            iterations: 0,
            x: new Float64Array(0),
            clampEvents: 0,
            finalResidualNorm: 0,
        };
    }
    let x = new Float64Array(opts.x0);
    let iterations = 0;
    let clampEvents = 0;
    let finalResidualNorm = 0;
    // Sprint 7 pnjlim: per-transient-step junction voltage history.
    // Shared across all Newton iterations of THIS step so pnjlim sees
    // real iteration-to-iteration continuity; reset at the start of
    // each step because transient state has legitimately moved between
    // adjacent accepted steps.
    const junctionHistory = new Map();
    // Sprint 8a.1: per-step previous-delta state for sign-flip damping
    // (see `dc/newton-raphson.ts`). Reset per step — the "previous"
    // delta of iter 1 is meaningless across step boundaries.
    let previousDeltas = null;
    // Sprint 8b: previous Newton step delta for the reduced-KCL
    // residual convergence check (see `dc/newton-raphson.ts`).
    let previousStepDelta = null;
    const dampingEnabled = opts.enableDeltaVDamping ?? true;
    // Sprint 80.Z L-AYARLAR-5 — runtime temperature.
    const temperatureK = opts.temperature ?? REFERENCE_TEMPERATURE_K;
    const deltaVLimit = 10 * vtAtTemperature(temperatureK);
    for (let iter = 1; iter <= opts.maxIterations; iter++) {
        const { Y, rhs } = buildTransientMNA(circuit, plan, {
            xPrev: x,
            xStepPrev: opts.xStepPrev,
            xStepPrevPrev: opts.xStepPrevPrev,
            t: opts.t,
            h: opts.h,
            hPrev: opts.hPrev,
            method: opts.method,
            firstStep: opts.firstStep,
            previousCurrent: opts.previousCurrent,
            gmin: opts.gmin ?? 0,
            sourceScale: opts.sourceScale ?? 1,
            junctionHistory,
            enableDeltaVDamping: dampingEnabled,
            temperature: temperatureK,
        });
        // Sprint 8b: top-of-loop residual check — see `dc/newton-raphson.ts`
        // for rationale (Y·x − rhs after LU is identically zero, so the
        // check must use the NEXT iter's fresh build to evaluate f(x)).
        if (previousStepDelta !== null) {
            const residual = computeResidual(Y, x, rhs);
            let residMax = 0;
            for (const i of plan.nodeIndexSet) {
                const r = residual[i] ?? 0;
                const abs = Math.abs(r);
                if (abs > residMax)
                    residMax = abs;
            }
            finalResidualNorm = residMax;
            const converged = isConvergedReduced({
                x,
                residual,
                deltaX: previousStepDelta,
                nodeIndexSet: plan.nodeIndexSet,
                tier: "strict",
            });
            if (converged) {
                return {
                    converged: true,
                    iterations: iter - 1,
                    x,
                    clampEvents,
                    finalResidualNorm,
                };
            }
        }
        const xNewRaw = sparseSolve(Y, rhs);
        iterations = iter;
        if (!isFiniteVector(xNewRaw)) {
            throw new ConvergenceError(`Newton-Raphson (transient) produced non-finite iterate at step ${String(iter)}`, {
                meta: {
                    iterations: iter,
                    t: opts.t,
                    h: opts.h,
                    method: opts.method,
                },
            });
        }
        // Sprint 8a: trust-region step scaling, same helper as DC.
        // Sprint 8a.1: monotonic-climb skip via `previousDeltas` state.
        // Linear RC/RL transients see alpha = 1 every iter (junction set
        // empty); diode / BJT transients get per-iter scaling.
        let xNew;
        if (dampingEnabled && plan.junctionBearingIndices.size > 0) {
            const damped = applyDeltaVDamping(xNewRaw, x, plan.junctionBearingIndices, deltaVLimit, previousDeltas);
            xNew = damped.result;
            if (damped.alpha < 1)
                clampEvents += 1;
            previousDeltas = damped.junctionDeltas;
        }
        else {
            xNew = xNewRaw;
        }
        // Record step delta for next iter's convergence check.
        const stepDelta = new Float64Array(xNew.length);
        for (let i = 0; i < xNew.length; i++)
            stepDelta[i] = (xNew[i] ?? 0) - (x[i] ?? 0);
        previousStepDelta = stepDelta;
        x = xNew;
    }
    // Final build to evaluate residual at the last iterate.
    if (previousStepDelta !== null) {
        const { Y: Yf, rhs: rhsf } = buildTransientMNA(circuit, plan, {
            xPrev: x,
            xStepPrev: opts.xStepPrev,
            xStepPrevPrev: opts.xStepPrevPrev,
            t: opts.t,
            h: opts.h,
            hPrev: opts.hPrev,
            method: opts.method,
            firstStep: opts.firstStep,
            previousCurrent: opts.previousCurrent,
            gmin: opts.gmin ?? 0,
            junctionHistory,
            enableDeltaVDamping: dampingEnabled,
            temperature: temperatureK,
        });
        const residual = computeResidual(Yf, x, rhsf);
        let residMax = 0;
        for (const i of plan.nodeIndexSet) {
            const r = residual[i] ?? 0;
            const abs = Math.abs(r);
            if (abs > residMax)
                residMax = abs;
        }
        finalResidualNorm = residMax;
        const converged = isConvergedReduced({
            x,
            residual,
            deltaX: previousStepDelta,
            nodeIndexSet: plan.nodeIndexSet,
            tier: "strict",
        });
        if (converged) {
            return { converged: true, iterations, x, clampEvents, finalResidualNorm };
        }
    }
    return { converged: false, iterations, x, clampEvents, finalResidualNorm };
}
//# sourceMappingURL=newton-raphson.js.map